import React, { useState, useMemo, useEffect } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { PACKAGES, MATCHES, CATERING_INFO, formatCzechDate, fmtPrice } from '../data/beraniSkybox';

if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const LOGO_BERANI = 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/dtk3lr4t_logo%20berani.png';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ============================================================
// ============= Utility helpers ==============================
// ============================================================
const daysBetween = (a, b) => {
  const ONE = 24 * 60 * 60 * 1000;
  return Math.round((new Date(a) - new Date(b)) / ONE);
};

const isPastCutoff = (matchDateIso) => {
  if (!matchDateIso) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return daysBetween(matchDateIso, today) < CATERING_INFO.minDaysBeforeMatch;
};

const cutoffDate = (matchDateIso) => {
  const d = new Date(matchDateIso);
  d.setDate(d.getDate() - CATERING_INFO.minDaysBeforeMatch);
  return d.toISOString().slice(0, 10);
};

// ============================================================
// ============= Icons ========================================
// ============================================================
const Ico = ({ children, className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const ChevronRight = (p) => <Ico {...p}><path d="M9 18l6-6-6-6" /></Ico>;
const Check = (p) => <Ico {...p}><path d="M5 12l5 5L20 7" /></Ico>;
const Download = (p) => <Ico {...p}><path d="M12 4v12m0 0l-5-5m5 5l5-5M5 21h14" /></Ico>;
const AlertIcon = (p) => <Ico {...p}><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></Ico>;

// ============================================================
// ============= Main component ===============================
// ============================================================
const BeraniSkyboxPage = () => {
  const [step, setStep] = useState(1); // 1 select, 2 form, 3 recap, 4 done
  const [quantities, setQuantities] = useState(() => Object.fromEntries(PACKAGES.map((p) => [p.id, 0])));
  const [customer, setCustomer] = useState({
    company_name: '',
    ico: '',
    email: '',
    phone: '',
    match_id: '',
    skybox_number: '',
    person_count: CATERING_INFO.fixedPersons,
    note: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const selectedItems = useMemo(
    () => PACKAGES.map((p) => ({ ...p, qty: quantities[p.id] || 0 })).filter((p) => p.qty > 0),
    [quantities]
  );
  const totalPrice = useMemo(() => selectedItems.reduce((s, p) => s + p.qty * p.price, 0), [selectedItems]);
  const totalPacks = selectedItems.reduce((s, p) => s + p.qty, 0);

  const selectedMatch = useMemo(
    () => MATCHES.find((m) => String(m.id) === String(customer.match_id)),
    [customer.match_id]
  );
  const cutoffTooLate = selectedMatch ? isPastCutoff(selectedMatch.date) : false;

  const updateQty = (id, delta) =>
    setQuantities((q) => ({ ...q, [id]: Math.max(0, Math.min(20, (q[id] || 0) + delta)) }));

  const setQty = (id, val) =>
    setQuantities((q) => ({ ...q, [id]: Math.max(0, Math.min(20, parseInt(val || 0, 10))) }));

  const validateForm = () => {
    const e = {};
    if (!customer.company_name.trim()) e.company_name = 'Zadejte název subjektu';
    if (!/^\d{6,10}$/.test(customer.ico.trim())) e.ico = 'IČO musí obsahovat 6–10 číslic';
    if (!/^\S+@\S+\.\S+$/.test(customer.email.trim())) e.email = 'Zadejte platný e-mail';
    if (!customer.phone.trim() || customer.phone.trim().length < 6) e.phone = 'Zadejte platný telefon';
    if (!customer.match_id) e.match_id = 'Vyberte zápas';
    if (!customer.skybox_number.trim()) e.skybox_number = 'Zadejte číslo SKYBOXU';
    if (cutoffTooLate) e.match_id = 'Na tento zápas již není možné catering objednat';
    if (totalPacks === 0) e.items = 'Vyberte alespoň jeden balíček';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useEffect(() => window.scrollTo(0, 0), [step]);

  const goToForm = () => {
    if (totalPacks === 0) {
      setErrors({ items: 'Vyberte alespoň jeden balíček' });
      return;
    }
    setErrors({});
    setStep(2);
  };

  const goToRecap = () => {
    if (validateForm()) setStep(3);
  };

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        company_name: customer.company_name.trim(),
        ico: customer.ico.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
        match_date: selectedMatch.date,
        match_time: selectedMatch.time,
        opponent: selectedMatch.opponent,
        skybox_number: customer.skybox_number.trim(),
        person_count: customer.person_count,
        note: customer.note.trim(),
        items: selectedItems.map((p) => ({
          package_id: p.id,
          package_code: p.code,
          package_name: p.name,
          quantity: p.qty,
          unit_price: p.price,
        })),
      };
      const res = await fetch(`${API}/berani/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Chyba při odeslání');
      setSubmitResult(data.order);
      setStep(4);
    } catch (e) {
      alert('Chyba: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    const order = submitResult || {
      order_number: 'PREVIEW',
      company_name: customer.company_name,
      ico: customer.ico,
      email: customer.email,
      phone: customer.phone,
      opponent: selectedMatch?.opponent,
      match_date: selectedMatch?.date,
      match_time: selectedMatch?.time,
      skybox_number: customer.skybox_number,
      person_count: customer.person_count,
      note: customer.note,
      items: selectedItems.map((p) => ({
        package_code: p.code,
        package_name: p.name,
        quantity: p.qty,
        unit_price: p.price,
      })),
      total_price: totalPrice,
    };
    pdfMake.createPdf(buildOrderPdf(order)).download(`Objednavka_${order.order_number}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-slate-100" data-testid="berani-skybox-page">
      <HeroHeader step={step} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {step === 1 && (
          <Step1Select
            quantities={quantities}
            updateQty={updateQty}
            setQty={setQty}
            selectedItems={selectedItems}
            totalPrice={totalPrice}
            errors={errors}
            onContinue={goToForm}
          />
        )}
        {step === 2 && (
          <Step2Form
            customer={customer}
            setCustomer={setCustomer}
            errors={errors}
            selectedMatch={selectedMatch}
            cutoffTooLate={cutoffTooLate}
            selectedItems={selectedItems}
            totalPrice={totalPrice}
            onBack={() => setStep(1)}
            onContinue={goToRecap}
          />
        )}
        {step === 3 && (
          <Step3Recap
            customer={customer}
            selectedMatch={selectedMatch}
            selectedItems={selectedItems}
            totalPrice={totalPrice}
            submitting={submitting}
            onBack={() => setStep(2)}
            onSubmit={submitOrder}
          />
        )}
        {step === 4 && submitResult && (
          <Step4Done order={submitResult} onDownload={downloadPdf} />
        )}
      </div>
      <Footer />
    </div>
  );
};

// ============================================================
// ============= Header ========================================
// ============================================================
const HeroHeader = ({ step }) => (
  <header className="relative overflow-hidden">
    {/* stripe */}
    <div className="absolute top-0 left-0 right-0 h-1 flex">
      <div className="flex-1 bg-[#FCD34D]" />
      <div className="flex-1 bg-[#1e3a8a]" />
      <div className="flex-1 bg-[#FCD34D]" />
    </div>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-8 sm:pt-16 sm:pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 min-w-0">
          <div className="relative shrink-0">
            {/* blurred glow — silnější na mobilu, jemnější na desktopu */}
            <div
              aria-hidden
              className="absolute inset-0 rounded-full bg-white/55 blur-xl scale-[1.45] sm:bg-white/30 sm:blur-2xl sm:scale-110"
            />
            <img
              src={LOGO_BERANI}
              alt="RI OKNA Berani Zlín"
              className="relative h-16 sm:h-24 lg:h-28 w-auto drop-shadow-2xl"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.35em] text-[#FCD34D] mb-3 font-semibold">
              RI OKNA · BERANI ZLÍN · VIP CATERING
            </div>
            <h1 className="font-black leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-white">
              BERANI <span className="text-[#FCD34D]">VIP</span> SKYBOX <span className="text-[#FCD34D]">CATERING</span>
            </h1>
            <p className="mt-4 text-slate-300 text-sm sm:text-base max-w-xl">
              Prémiový catering přímo do vašeho SKYBOXU. Objednávka za 2 minuty. Realizuje <strong className="text-white">VšeProAkci s.r.o.</strong>
            </p>
          </div>
        </div>
        <StepIndicator step={step} />
      </div>
    </div>
  </header>
);

const StepIndicator = ({ step }) => {
  const steps = ['Výběr', 'Údaje', 'Rekapitulace', 'Potvrzení'];
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto -mx-4 sm:-mx-0 px-4 sm:px-0 pb-1 sm:pb-0 flex-wrap sm:flex-nowrap">
      {steps.map((s, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <React.Fragment key={s}>
            <div
              className={`px-3 py-1.5 rounded-full text-[10px] tracking-widest font-bold transition-colors ${
                active
                  ? 'bg-[#1e3a8a] text-white'
                  : done
                  ? 'bg-[#FCD34D] text-slate-900'
                  : 'bg-slate-800 text-slate-500'
              }`}
            >
              {String(idx).padStart(2, '0')} · {s.toUpperCase()}
            </div>
            {i < steps.length - 1 && <div className="w-3 h-px bg-slate-700" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ============================================================
// ============= Step 1: Package Selection =====================
// ============================================================
const Step1Select = ({ quantities, updateQty, setQty, selectedItems, totalPrice, errors, onContinue }) => (
  <div className="grid lg:grid-cols-[1fr_320px] gap-8">
    <div>
      <SectionEyebrow>KROK 1</SectionEyebrow>
      <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">Vyberte cateringové balíčky</h2>
      <p className="text-slate-400 mb-8 max-w-2xl">
        5 pečlivě sestavených VIP balíčků od šéfkuchařů VšeProAkci. Každý balíček je určen pro 4 osoby v jednom SKYBOXU. Můžete kombinovat více balíčků a upravit množství.
      </p>

      <div className="space-y-6">
        {PACKAGES.map((p) => (
          <PackageCard
            key={p.id}
            pkg={p}
            qty={quantities[p.id] || 0}
            onInc={() => updateQty(p.id, 1)}
            onDec={() => updateQty(p.id, -1)}
            onSet={(v) => setQty(p.id, v)}
          />
        ))}
      </div>

      {errors.items && (
        <div className="mt-6 p-4 rounded-lg bg-[#1e3a8a]/20 border border-[#1e3a8a]/60 text-[#FCA5A5] flex items-start gap-3">
          <AlertIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{errors.items}</span>
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="text-slate-400 text-sm">
          Celkem <strong className="text-white">{selectedItems.reduce((s, i) => s + i.qty, 0)}</strong> balíčků ·{' '}
          <strong className="text-[#FCD34D]">{fmtPrice(totalPrice)}</strong> s DPH
        </div>
        <button
          data-testid="continue-to-form-btn"
          onClick={onContinue}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] transition-colors text-white font-bold tracking-wide uppercase text-sm shadow-lg shadow-[#1e3a8a]/30"
        >
          POKRAČOVAT K OBJEDNÁVCE
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>

    {/* Sticky summary desktop */}
    <div className="hidden lg:block">
      <StickySummary selectedItems={selectedItems} totalPrice={totalPrice} />
    </div>
  </div>
);

const SectionEyebrow = ({ children }) => (
  <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] mb-3 font-bold">{children}</div>
);

const PackageCard = ({ pkg, qty, onInc, onDec, onSet }) => (
  <div
    data-testid={`package-card-${pkg.id}`}
    className={`relative rounded-2xl overflow-hidden border transition-all ${
      qty > 0 ? 'border-[#FCD34D] bg-slate-900/80 shadow-xl shadow-[#FCD34D]/10' : 'border-slate-800 bg-slate-900/40'
    }`}
  >
    {/* accent stripe */}
    <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: pkg.accent }} />

    <div className="grid md:grid-cols-[220px_1fr] gap-6 p-6 pl-8">
      {/* visual */}
      <div
        className="rounded-xl flex flex-col items-center justify-center p-6 min-h-[180px]"
        style={{ background: `linear-gradient(135deg, ${pkg.accent}22, ${pkg.accent}55)` }}
      >
        <div className="text-6xl font-black text-white/90 leading-none">{pkg.code}</div>
        <div className="text-[10px] tracking-widest text-white/70 mt-2">BALÍČEK</div>
        <div className="mt-6 text-[10px] tracking-widest text-white/60">{pkg.persons.toUpperCase()}</div>
      </div>

      {/* content */}
      <div>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-xl sm:text-2xl font-black text-white leading-tight">{pkg.name}</h3>
            <p className="text-slate-400 text-sm mt-1">{pkg.tagline}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl sm:text-3xl font-black text-[#FCD34D]">{fmtPrice(pkg.price)}</div>
            <div className="text-[10px] text-slate-500 tracking-wider">S DPH / SKYBOX</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">JÍDLO</div>
            <ul className="text-[13px] text-slate-300 space-y-1">
              {pkg.food.map((f, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#FCD34D] shrink-0">·</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">NÁPOJE — CHLAZENÉ VE SKYBOXU</div>
            <ul className="text-[13px] text-slate-300 space-y-1">
              {pkg.drinks.map((f, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#FCD34D] shrink-0">·</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* quantity picker */}
        <div className="mt-6 flex items-center justify-between border-t border-slate-800 pt-4">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Počet balíčků</div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`dec-${pkg.id}`}
              onClick={onDec}
              disabled={qty === 0}
              className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-white text-lg font-bold flex items-center justify-center transition-colors"
            >
              −
            </button>
            <input
              data-testid={`qty-${pkg.id}`}
              type="number"
              min={0}
              max={20}
              value={qty}
              onChange={(e) => onSet(e.target.value)}
              className="w-14 h-10 rounded-lg bg-slate-800 text-white text-center font-bold text-lg border border-slate-700 focus:border-[#FCD34D] focus:outline-none"
            />
            <button
              data-testid={`inc-${pkg.id}`}
              onClick={onInc}
              className="w-10 h-10 rounded-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white text-lg font-bold flex items-center justify-center transition-colors"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StickySummary = ({ selectedItems, totalPrice }) => (
  <div className="sticky top-6 rounded-2xl bg-slate-900 border border-slate-800 p-6 shadow-2xl" data-testid="sticky-summary">
    <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-2">VAŠE OBJEDNÁVKA</div>
    <div className="text-lg font-bold text-white mb-4">Rekapitulace</div>
    {selectedItems.length === 0 ? (
      <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-800 rounded-lg">
        Zatím jste nevybrali žádný balíček
      </div>
    ) : (
      <div className="space-y-3">
        {selectedItems.map((p) => (
          <div key={p.id} className="flex justify-between items-start pb-3 border-b border-slate-800 last:border-0">
            <div>
              <div className="text-sm text-white font-medium">{p.qty}× {p.name}</div>
              <div className="text-xs text-slate-500">{fmtPrice(p.price)} / balíček</div>
            </div>
            <div className="text-sm font-bold text-white font-mono">{fmtPrice(p.qty * p.price)}</div>
          </div>
        ))}
      </div>
    )}
    <div className="mt-4 pt-4 border-t-2 border-[#FCD34D]/40 flex justify-between items-baseline">
      <div className="text-xs text-slate-400 uppercase tracking-widest">Celkem</div>
      <div className="text-2xl font-black text-[#FCD34D]">{fmtPrice(totalPrice)}</div>
    </div>
    <div className="text-[10px] text-slate-500 mt-1 text-right">s DPH</div>
  </div>
);

// ============================================================
// ============= Step 2: Customer form =========================
// ============================================================
const Step2Form = ({ customer, setCustomer, errors, selectedMatch, cutoffTooLate, selectedItems, totalPrice, onBack, onContinue }) => {
  const upd = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8">
      <div>
        <SectionEyebrow>KROK 2</SectionEyebrow>
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">Údaje objednavatele</h2>
        <p className="text-slate-400 mb-8 max-w-2xl">
          Vyplňte fakturační údaje a upřesněte, na který zápas catering objednáváte.
        </p>

        {/* Match selection */}
        <div className="mb-8">
          <div className="text-[10px] tracking-widest text-[#FCD34D] font-bold mb-3">VYBERTE ZÁPAS</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {MATCHES.map((m) => {
              const past = isPastCutoff(m.date);
              const active = String(customer.match_id) === String(m.id);
              return (
                <button
                  key={m.id}
                  data-testid={`match-${m.id}`}
                  type="button"
                  disabled={past}
                  onClick={() => setCustomer((c) => ({ ...c, match_id: m.id }))}
                  className={`text-left rounded-lg border p-4 transition-all relative ${
                    past
                      ? 'border-slate-800 bg-slate-900/40 opacity-40 cursor-not-allowed'
                      : active
                      ? 'border-[#FCD34D] bg-[#1e3a8a]/20 shadow-lg shadow-[#FCD34D]/10'
                      : 'border-slate-800 bg-slate-900/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="text-[10px] tracking-widest text-slate-500">{m.round} · {m.day.toUpperCase()}</div>
                    {active && <Check className="w-4 h-4 text-[#FCD34D]" />}
                  </div>
                  <div className="text-lg font-bold text-white mt-1 leading-tight">RI OKNA Berani Zlín — {m.opponent}</div>
                  <div className="text-sm text-slate-400 mt-1">{formatCzechDate(m.date)} · {m.time}</div>
                  {past && (
                    <div className="mt-2 text-[11px] text-[#FCA5A5]">
                      Uzávěrka objednávek: {formatCzechDate(cutoffDate(m.date))} — již uplynula
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {errors.match_id && (
            <div className="mt-3 text-sm text-[#FCA5A5]">{errors.match_id}</div>
          )}
          {selectedMatch && !cutoffTooLate && (
            <div className="mt-4 p-3 rounded-lg bg-[#FCD34D]/10 border border-[#FCD34D]/30 text-[13px] text-[#FCD34D]">
              ⚠️ Objednávku je nutné odeslat nejpozději do <strong>{formatCzechDate(cutoffDate(selectedMatch.date))}</strong> — minimálně 5 dnů před utkáním.
            </div>
          )}
          {cutoffTooLate && (
            <div className="mt-4 p-4 rounded-lg bg-[#1e3a8a]/25 border border-[#1e3a8a]/70 text-[#FCA5A5] flex items-start gap-3">
              <AlertIcon className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">Na toto utkání již není možné catering objednat.</div>
                <div className="text-sm mt-1">Objednávku je nutné provést minimálně 5 dnů před zahájením utkání.</div>
              </div>
            </div>
          )}
        </div>

        {/* Customer info form */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Název subjektu / firmy *" error={errors.company_name}>
            <input data-testid="input-company" value={customer.company_name} onChange={upd('company_name')} className={inputCls(errors.company_name)} />
          </Field>
          <Field label="IČO *" error={errors.ico}>
            <input data-testid="input-ico" value={customer.ico} onChange={upd('ico')} placeholder="12345678" className={inputCls(errors.ico)} />
          </Field>
          <Field label="E-mail *" error={errors.email}>
            <input data-testid="input-email" type="email" value={customer.email} onChange={upd('email')} className={inputCls(errors.email)} />
          </Field>
          <Field label="Telefon *" error={errors.phone}>
            <input data-testid="input-phone" value={customer.phone} onChange={upd('phone')} placeholder="+420 ..." className={inputCls(errors.phone)} />
          </Field>
          <Field label="Číslo / označení SKYBOXU *" error={errors.skybox_number}>
            <input data-testid="input-skybox" value={customer.skybox_number} onChange={upd('skybox_number')} placeholder="např. B12" className={inputCls(errors.skybox_number)} />
          </Field>
        </div>

        <Field label="Poznámka k objednávce (volitelné)">
          <textarea data-testid="input-note" value={customer.note} onChange={upd('note')} rows={3} className={inputCls(false)} placeholder="např. alergie, preferované servírování, čas doručení do SKYBOXU..." />
        </Field>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-between">
          <button
            data-testid="back-to-select-btn"
            onClick={onBack}
            className="px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-sm tracking-wider"
          >
            ← ZPĚT NA VÝBĚR
          </button>
          <button
            data-testid="continue-to-recap-btn"
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-bold tracking-wide uppercase text-sm shadow-lg shadow-[#1e3a8a]/30"
          >
            POKRAČOVAT K REKAPITULACI
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="hidden lg:block">
        <StickySummary selectedItems={selectedItems} totalPrice={totalPrice} />
      </div>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div className="mt-4">
    <label className="text-[11px] tracking-widest text-slate-400 font-semibold uppercase">{label}</label>
    {children}
    {error && <div className="mt-1 text-xs text-[#FCA5A5]">{error}</div>}
  </div>
);

const inputCls = (hasError) =>
  `mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/70 border ${
    hasError ? 'border-[#1e3a8a]' : 'border-slate-700'
  } text-white placeholder:text-slate-600 focus:border-[#FCD34D] focus:outline-none transition-colors`;

// ============================================================
// ============= Step 3: Recap =================================
// ============================================================
const Step3Recap = ({ customer, selectedMatch, selectedItems, totalPrice, submitting, onBack, onSubmit }) => (
  <div>
    <SectionEyebrow>KROK 3</SectionEyebrow>
    <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">Rekapitulace objednávky</h2>
    <p className="text-slate-400 mb-8 max-w-2xl">Před odesláním prosím zkontrolujte správnost údajů. Po potvrzení bude objednávka odeslána na <strong className="text-white">catering@vseproakci.cz</strong> a vytvořeno PDF pro vaši evidenci.</p>

    <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden shadow-2xl">
      {/* header banner */}
      <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] px-6 py-6 border-b border-slate-800">
        <div className="text-[10px] tracking-widest text-[#FCD34D] font-bold">OBJEDNÁVKA · VIP SKYBOX CATERING</div>
        <div className="text-2xl font-black text-white mt-1">RI OKNA Berani Zlín — {selectedMatch?.opponent}</div>
        <div className="text-slate-400 text-sm mt-1">{selectedMatch && formatCzechDate(selectedMatch.date)} · {selectedMatch?.time}</div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 p-6 border-b border-slate-800">
        <div>
          <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">OBJEDNAVATEL</div>
          <div className="text-white font-bold text-lg">{customer.company_name}</div>
          <div className="text-sm text-slate-400 mt-1">IČO: {customer.ico}</div>
          <div className="text-sm text-slate-400">{customer.email}</div>
          <div className="text-sm text-slate-400">{customer.phone}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">DETAIL AKCE</div>
          <RecapRow label="SKYBOX" value={customer.skybox_number} />
          <RecapRow label="Datum utkání" value={selectedMatch && formatCzechDate(selectedMatch.date)} />
          <RecapRow label="Začátek" value={selectedMatch?.time} />
        </div>
      </div>

      <div className="p-6">
        <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-3">OBJEDNANÉ BALÍČKY</div>
        <div className="rounded-lg border border-slate-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70 text-slate-400 text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">BALÍČEK</th>
                <th className="px-4 py-3 text-center">POČET</th>
                <th className="px-4 py-3 text-right">CENA</th>
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((p) => (
                <tr key={p.id} className="border-t border-slate-800">
                  <td className="px-4 py-3 text-white">
                    <span className="text-[#FCD34D] font-mono mr-2">{p.code}</span>{p.name}
                  </td>
                  <td className="px-4 py-3 text-center text-white font-bold">{p.qty}×</td>
                  <td className="px-4 py-3 text-right text-white font-mono">{fmtPrice(p.qty * p.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {customer.note && (
          <div className="mt-4 p-4 rounded-lg bg-slate-800/60 border border-slate-700">
            <div className="text-[10px] tracking-widest text-slate-400 mb-1">POZNÁMKA</div>
            <div className="text-sm text-slate-200">{customer.note}</div>
          </div>
        )}
      </div>

      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] px-6 py-6 flex items-baseline justify-between">
        <div className="text-[10px] tracking-widest text-white/80 font-bold">CELKEM K ÚHRADĚ</div>
        <div className="text-4xl font-black text-white">{fmtPrice(totalPrice)} <span className="text-sm font-normal text-white/70">s DPH</span></div>
      </div>
    </div>

    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-between">
      <button
        data-testid="back-to-form-btn"
        onClick={onBack}
        className="px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-sm tracking-wider"
      >
        ← ZPĚT NA ÚDAJE
      </button>
      <button
        data-testid="submit-order-btn"
        onClick={onSubmit}
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FCD34D] hover:bg-[#FBB040] text-slate-900 font-bold tracking-wide uppercase text-sm shadow-lg shadow-[#FCD34D]/30 disabled:opacity-60"
      >
        {submitting ? 'ODESÍLÁM…' : 'GENEROVAT OBJEDNÁVKU'}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const RecapRow = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-800 last:border-0">
    <span className="text-sm text-slate-400">{label}</span>
    <span className="text-sm text-white font-medium">{value || '—'}</span>
  </div>
);

// ============================================================
// ============= Step 4: Done ==================================
// ============================================================
const Step4Done = ({ order, onDownload }) => (
  <div className="max-w-2xl mx-auto text-center">
    <div className="mx-auto w-20 h-20 rounded-full bg-[#FCD34D] text-slate-900 flex items-center justify-center shadow-lg shadow-[#FCD34D]/30">
      <Check className="w-10 h-10" />
    </div>
    <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mt-6 mb-3">OBJEDNÁVKA JE PŘIPRAVENA</div>
    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Děkujeme za objednávku</h2>
    <p className="text-slate-300 leading-relaxed max-w-lg mx-auto">
      Objednávka <strong className="text-white font-mono">{order.order_number}</strong> byla úspěšně odeslána na <strong className="text-white">{CATERING_INFO.email}</strong> a zároveň jsme vám zaslali potvrzení na <strong className="text-white">{order.email}</strong>.
    </p>
    <p className="text-slate-300 leading-relaxed max-w-lg mx-auto mt-3">
      Náš tým vás bude co nejdříve kontaktovat pro potvrzení všech detailů.
    </p>

    <div className="mt-8 rounded-2xl bg-slate-900 border border-slate-800 p-6 text-left">
      <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">SHRNUTÍ</div>
      <div className="text-white font-bold text-lg">RI OKNA Berani Zlín — {order.opponent}</div>
      <div className="text-sm text-slate-400 mt-1">{formatCzechDate(order.match_date)}{order.match_time ? ' · ' + order.match_time : ''}</div>
      <div className="text-sm text-slate-400 mt-3">SKYBOX <strong className="text-white">{order.skybox_number}</strong> · {order.person_count} osob</div>
      <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between items-baseline">
        <div className="text-slate-400 text-sm">Celkem</div>
        <div className="text-2xl font-black text-[#FCD34D]">{fmtPrice(order.total_price)} <span className="text-xs text-slate-500 font-normal">s DPH</span></div>
      </div>
    </div>

    <div className="mt-8 flex justify-center">
      <button
        data-testid="download-pdf-btn"
        onClick={onDownload}
        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FCD34D] hover:bg-[#FBB040] text-slate-900 font-bold tracking-wide uppercase text-sm shadow-lg shadow-[#FCD34D]/30"
      >
        <Download className="w-4 h-4" />
        STÁHNOUT PDF S OBJEDNÁVKOU
      </button>
    </div>

    <div className="mt-6 text-xs text-slate-500">
      PDF slouží pro vaši evidenci. Kopie objednávky již byla odeslána cateringovému týmu.
    </div>
  </div>
);

// ============================================================
// ============= Footer =========================================
// ============================================================
const Footer = () => (
  <footer className="border-t border-slate-800 mt-12">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-3 gap-6 text-sm">
      <div>
        <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-2">CATERING</div>
        <div className="text-white font-bold">VšeProAkci s.r.o.</div>
        <div className="text-slate-400 mt-1">Event catering · VIP catering · Food trucky · Kompletní eventová produkce</div>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-2">OBJEDNÁVKY</div>
        <a href={`mailto:${CATERING_INFO.email}`} className="text-white hover:text-[#FCD34D]">{CATERING_INFO.email}</a>
        <div className="text-slate-400 mt-1">Objednávky min. 5 dnů před utkáním</div>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-2">PARTNER</div>
        <div className="text-white font-bold">RI OKNA Berani Zlín</div>
        <div className="text-slate-400 mt-1">VIP SKYBOX Catering — sezóna 2026/2027</div>
      </div>
    </div>
    <div className="h-1 flex">
      <div className="flex-1 bg-[#FCD34D]" />
      <div className="flex-1 bg-[#1e3a8a]" />
      <div className="flex-1 bg-[#FCD34D]" />
    </div>
  </footer>
);

// ============================================================
// ============= PDF Generator ==================================
// ============================================================
const buildOrderPdf = (order) => {
  const C = {
    ink: '#0f172a',
    ink2: '#334155',
    muted: '#64748B',
    hair: '#E2E8F0',
    paper: '#F8FAFC',
    red: '#1e3a8a',
    gold: '#F59E0B',
    blue: '#1e40af',
    white: '#ffffff',
  };

  const fmt = (n) => n.toLocaleString('cs-CZ') + ' Kč';

  const itemRows = order.items.map((it, i) => [
    { text: it.package_code, fontSize: 10, bold: true, color: C.red, alignment: 'center', margin: [0, 6, 0, 6] },
    { stack: [
      { text: it.package_name, fontSize: 11, bold: true, color: C.ink, margin: [0, 4, 0, 2] },
      { text: 'VIP SKYBOX catering pro 4–6 osob', fontSize: 8, color: C.muted, margin: [0, 0, 0, 4] },
    ] },
    { text: `${it.quantity}×`, fontSize: 12, bold: true, color: C.ink, alignment: 'center', margin: [0, 8, 0, 6] },
    { text: fmt(it.unit_price), fontSize: 10, color: C.ink2, alignment: 'right', margin: [0, 8, 0, 6] },
    { text: fmt(it.quantity * it.unit_price), fontSize: 11, bold: true, color: C.ink, alignment: 'right', margin: [0, 8, 0, 6] },
  ]);

  const nowStr = new Date().toLocaleDateString('cs-CZ') + ' ' + new Date().toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });

  return {
    pageSize: 'A4',
    pageMargins: [40, 45, 40, 60],
    background: (page, ps) => ({
      canvas: [
        // top stripe
        { type: 'rect', x: 0, y: 0, w: ps.width / 3, h: 4, color: C.gold },
        { type: 'rect', x: ps.width / 3, y: 0, w: ps.width / 3, h: 4, color: C.blue },
        { type: 'rect', x: (ps.width / 3) * 2, y: 0, w: ps.width / 3, h: 4, color: C.red },
      ],
    }),
    footer: (curr, total) => ({
      table: {
        widths: ['*', 'auto'],
        body: [[
          { text: `Objednávka ${order.order_number}  ·  RI OKNA Berani Zlín  ·  VIP Skybox Catering  ·  Realizuje VšeProAkci s.r.o.`, fontSize: 7, color: C.muted, margin: [40, 14, 0, 0], border: [false, true, false, false], borderColor: [C.hair, C.hair, C.hair, C.hair] },
          { text: `${curr} / ${total}`, fontSize: 8, color: C.red, alignment: 'right', bold: true, margin: [0, 14, 40, 0], border: [false, true, false, false], borderColor: [C.hair, C.hair, C.hair, C.hair] },
        ]],
      },
      layout: { hLineWidth: (i) => (i === 0 ? 0.5 : 0), vLineWidth: () => 0, hLineColor: () => C.hair },
    }),
    content: [
      // HERO
      { text: '', margin: [0, 10, 0, 0] },
      { text: 'BERANI VIP SKYBOX CATERING', fontSize: 8, color: C.gold, characterSpacing: 3, margin: [0, 0, 0, 4] },
      { text: 'OBJEDNÁVKA CATERINGU', fontSize: 22, bold: true, color: C.ink, margin: [0, 0, 0, 4] },
      { canvas: [{ type: 'rect', x: 0, y: 0, w: 46, h: 3, color: C.red }], margin: [0, 0, 0, 8] },
      {
        columns: [
          { text: `Číslo: ${order.order_number}`, fontSize: 10, color: C.ink2, bold: true },
          { text: `Vytvořeno: ${nowStr}`, fontSize: 10, color: C.muted, alignment: 'right' },
        ],
        margin: [0, 0, 0, 22],
      },

      // Match banner
      {
        table: {
          widths: ['*'],
          body: [[
            {
              stack: [
                { text: 'ZÁPAS', fontSize: 8, color: C.gold, characterSpacing: 2, bold: true, margin: [0, 0, 0, 4] },
                { text: `RI OKNA Berani Zlín — ${order.opponent}`, fontSize: 18, bold: true, color: C.white, margin: [0, 0, 0, 4] },
                { text: `${formatCzechDate(order.match_date)}${order.match_time ? ' · ' + order.match_time : ''}`, fontSize: 11, color: '#CBD5E1', margin: [0, 0, 0, 12] },
                {
                  columns: [
                    { stack: [
                      { text: 'SKYBOX', fontSize: 7, color: C.gold, characterSpacing: 2, margin: [0, 0, 0, 2] },
                      { text: order.skybox_number, fontSize: 16, bold: true, color: C.white },
                    ] },
                    { stack: [
                      { text: 'POČET OSOB', fontSize: 7, color: C.gold, characterSpacing: 2, margin: [0, 0, 0, 2] },
                      { text: String(order.person_count), fontSize: 16, bold: true, color: C.white },
                    ] },
                  ],
                },
              ],
              fillColor: C.ink,
              border: [false, false, false, false],
              margin: [18, 16, 18, 16],
            },
          ]],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20],
      },

      // Customer
      { text: 'OBJEDNAVATEL', fontSize: 8, color: C.red, characterSpacing: 2, bold: true, margin: [0, 4, 0, 6] },
      {
        table: {
          widths: ['auto', '*'],
          body: [
            [{ text: 'Název', fontSize: 8, color: C.muted, characterSpacing: 1, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 12, 6] },
             { text: order.company_name, fontSize: 12, bold: true, color: C.ink, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] }],
            [{ text: 'IČO', fontSize: 8, color: C.muted, characterSpacing: 1, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 12, 6] },
             { text: order.ico, fontSize: 10, color: C.ink, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] }],
            [{ text: 'E-mail', fontSize: 8, color: C.muted, characterSpacing: 1, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 12, 6] },
             { text: order.email, fontSize: 10, color: C.ink, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] }],
            [{ text: 'Telefon', fontSize: 8, color: C.muted, characterSpacing: 1, border: [false, false, false, false], margin: [0, 6, 12, 6] },
             { text: order.phone, fontSize: 10, color: C.ink, border: [false, false, false, false], margin: [0, 6, 0, 6] }],
          ],
        },
        layout: { hLineColor: () => C.hair, hLineWidth: (i) => 0.5, vLineWidth: () => 0 },
        margin: [0, 0, 0, 22],
      },

      // Items
      { text: 'OBJEDNANÉ BALÍČKY', fontSize: 8, color: C.red, characterSpacing: 2, bold: true, margin: [0, 4, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: [30, '*', 40, 70, 70],
          body: [
            [
              { text: 'KÓD', fontSize: 8, color: C.muted, alignment: 'center', bold: true, characterSpacing: 1, fillColor: C.paper, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] },
              { text: 'BALÍČEK', fontSize: 8, color: C.muted, bold: true, characterSpacing: 1, fillColor: C.paper, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] },
              { text: 'POČET', fontSize: 8, color: C.muted, alignment: 'center', bold: true, characterSpacing: 1, fillColor: C.paper, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] },
              { text: 'CENA / KS', fontSize: 8, color: C.muted, alignment: 'right', bold: true, characterSpacing: 1, fillColor: C.paper, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] },
              { text: 'CELKEM', fontSize: 8, color: C.muted, alignment: 'right', bold: true, characterSpacing: 1, fillColor: C.paper, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair], margin: [0, 6, 0, 6] },
            ],
            ...itemRows.map((row) => row.map((cell) => ({ ...cell, border: [false, false, false, true], borderColor: [C.hair, C.hair, C.hair, C.hair] }))),
          ],
        },
        layout: { hLineColor: () => C.hair, hLineWidth: () => 0.5, vLineWidth: () => 0 },
        margin: [0, 0, 0, 14],
      },

      // TOTAL box
      {
        table: {
          widths: ['*'],
          body: [[
            {
              stack: [
                { text: 'CELKEM K ÚHRADĚ', fontSize: 8, color: C.gold, characterSpacing: 2, bold: true },
                { text: `${fmt(order.total_price)}`, fontSize: 32, bold: true, color: C.white, margin: [0, 4, 0, 0] },
                { text: 's DPH', fontSize: 10, color: '#CBD5E1', margin: [0, 0, 0, 0] },
              ],
              fillColor: C.ink,
              border: [false, false, false, false],
              alignment: 'right',
              margin: [20, 16, 20, 16],
            },
          ]],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 18],
      },

      order.note ? {
        stack: [
          { text: 'POZNÁMKA', fontSize: 8, color: C.red, characterSpacing: 2, bold: true, margin: [0, 4, 0, 6] },
          {
            table: { widths: ['*'], body: [[{ text: order.note, fontSize: 10, color: C.ink2, lineHeight: 1.4, fillColor: C.paper, border: [false, false, false, false], margin: [12, 10, 12, 10] }]] },
            layout: 'noBorders',
          },
        ],
        margin: [0, 0, 0, 18],
      } : { text: '', margin: [0, 0, 0, 0] },

      // Deadline info
      {
        table: {
          widths: ['*'],
          body: [[{
            stack: [
              { text: 'DŮLEŽITÉ · TERMÍN OBJEDNÁVKY', fontSize: 8, color: C.gold, characterSpacing: 2, bold: true, margin: [0, 0, 0, 4] },
              { text: 'Objednávku je nutné provést minimálně 5 dnů před zahájením utkání. Objednávka podléhá potvrzení ze strany VšeProAkci s.r.o.', fontSize: 9, color: C.ink2, lineHeight: 1.4 },
            ],
            fillColor: '#FEF3C7',
            border: [true, true, true, true],
            borderColor: ['#FCD34D', '#FCD34D', '#FCD34D', '#FCD34D'],
            margin: [14, 10, 14, 10],
          }]],
        },
        layout: { hLineWidth: () => 0.6, vLineWidth: () => 0.6, hLineColor: () => '#FCD34D', vLineColor: () => '#FCD34D' },
        margin: [0, 4, 0, 18],
      },
    ],
    defaultStyle: { font: 'Roboto' },
  };
};

export default BeraniSkyboxPage;
